import { useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import FormErrors from "components/formParts/formErrors"
import { useTranslation } from "react-i18next"
import { decodeFirebaseError } from "utils/firebaseHelpers"
import { LocationWrite, InputMaxLength } from "types"
import Callout from "components/callout/callout"
import TextInput from "components/inputs/textInput"
import TextAreaInput from "components/inputs/textareaInput"
import useStorages from "hooks/useStorages"
import useLocations from "hooks/useLocations"
import useStorageLimits from "hooks/useStorageLimits"
import LocationSelector from "components/inputs/locationSelector"
import { ReactComponent as AddIcon } from "assets/icons/add.svg"
import Section from "components/layout/section"
import Button from "components/inputs/button"
import useLayout from "hooks/useLayout"
import { logger } from "utils/secureLogger"
import ProcessingButton from "components/inputs/processingButton"
import Form from "components/forms/form"
import { ReactComponent as UpgradeIcon } from "assets/icons/upgrade.svg"

interface CreateLocationFormProps {
    storageId: string
    defaultParentLocation?: string
    onSuccess?: () => void
}

const CreateLocationForm: React.FC<CreateLocationFormProps> = ({ storageId, defaultParentLocation, onSuccess }) => {

    const { t } = useTranslation()
    const state = useLocation().state as { locationId: string }

    const navigation = useNavigate()

    const [name, setName] = useState(``)
    const [description, setDescription] = useState(``)
    const [notes, setNotes] = useState(``)

    const [errors, setErrors] = useState<string[]>([]);

    const { addToast } = useLayout()

    const {
        locations,
        allLocations,
        createLocationAsync,
        isCreating
    } = useLocations({ storageId })
    
    const { storage } = useStorages({ storageId })
    
    const currentLocationCount = allLocations?.length || 0
    const storagePlan = storage?.settings?.storagePlan
    const { canAddLocation, limits } = useStorageLimits({ 
        storagePlan,
        currentLocationCount 
    })
    
    const [parentLocation, setParentLocation] = useState<string>(
        defaultParentLocation ?? (state?.locationId ? state.locationId : `_`)
    )

    const handleSubmit = async (event: React.FormEvent) => {
        logger.debug('Create location form submitted', undefined, 'CreateLocation')
        setErrors([])
        event.preventDefault()
        if (storageId === undefined) return
        
        if (!canAddLocation) {
            setErrors([t('errorLocationLimitReached')])
            addToast({ text: t('errorLocationLimitReached'), colorScheme: 'red' })
            return
        }
        
        try {
            const data: LocationWrite = {
                ids: {
                    storageId: storageId,
                },
                _version: 1,
                name: name,
                description: description,
                notes: notes,
                relations: {
                    parentLocation: parentLocation === `_` ? undefined : parentLocation,
                },
                isDeleted: false,
            }
            const result = await createLocationAsync(data) as { locationId: string }
            const createdLocationId = result.locationId
            
            logger.info('Location created successfully', { createdLocation: result }, 'CreateLocation')
            addToast({ text: t('toastLocationCreated'), colorScheme: 'green' })
            if (onSuccess) {
                onSuccess()
            } else {
                navigation(`/storage/locations/${createdLocationId}`)
            }
        } catch (error: unknown) {
            const errorMessage = decodeFirebaseError(error instanceof Error ? error.message : String(error))
            setErrors([errorMessage])
            addToast({ text: t('toastLocationError'), colorScheme: 'red' })
            logger.error('Failed to create location', error)
        }
    }

    return (
        <Form
            className={`group`}
            onSubmit={handleSubmit}
            isLoading={isCreating}
        >
            {!canAddLocation && (
                <Section>
                    <Callout type="warning">
                        {t('errorLocationLimitReached', { limit: limits.locations })}
                        <Button wideOnMobile to={`/storage/edit`} design="primary"><UpgradeIcon /><span>{t('buttonUpgradeStoragePlan')}</span></Button>
                    </Callout>
                </Section>
            )}
            <Section>
                <TextInput label={t('labelLocationName')} required={true} value={name} onChange={(e) => setName(e.target.value)} maxLength={InputMaxLength.short} />
                <TextAreaInput label={t('description')} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={InputMaxLength.long} />
                <TextAreaInput label={t('notes')} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={InputMaxLength.extraLong} />
                <div className={`input-holder-wide`}>
                    <label>{t('labelParentLocation')}</label>
                    <LocationSelector
                        locations={locations}
                        allLocations={allLocations}
                        selectedLocation={parentLocation}
                        setSelectedLocation={setParentLocation}
                        allowCreate={false}
                    />
                </div>
            </Section>
            <Section>
                <div className={`input-holder`}>
                    {isCreating ? (
                        <ProcessingButton design="primary" />
                    ) : (
                        <Button wideOnMobile type="submit" design="primary" disabled={!canAddLocation}><AddIcon /><span>{t('buttonCreateLocation')}</span></Button>
                    )}
                </div>
                {errors.length > 0 && <FormErrors errors={errors} />}
            </Section>
        </Form>
    )

}

export default CreateLocationForm
